const OPERATOR_PATTERN = /^[()+*/-]$/;
const BUBBLE_AVAILABLE_FUNCTION = 'BubbleAvailable';
const FIRST_AVAILABLE_FUNCTION = 'FirstAvailable';
const EXPRESSION_FUNCTIONS = [BUBBLE_AVAILABLE_FUNCTION, FIRST_AVAILABLE_FUNCTION];

const getMetricMaps = (metrics) => {
  const sortedMetrics = metrics
    .filter((metric) => metric?.id)
    .sort((prev, next) => next.id.length - prev.id.length);
  return {
    metricById: new Map(sortedMetrics.map((metric) => [metric.id, metric])),
    metricIds: sortedMetrics.map((metric) => metric.id),
  };
};

export const tokenizeMetricExpression = (expression, metrics = []) => {
  const source = String(expression || '');
  const { metricById, metricIds } = getMetricMaps(metrics);
  const tokens = [];
  let index = 0;
  while (index < source.length) {
    const rest = source.slice(index);
    const whitespaceMatch = rest.match(/^\s+/);
    if (whitespaceMatch) {
      index += whitespaceMatch[0].length;
      continue;
    }
    const expressionFunction = EXPRESSION_FUNCTIONS.find((name) => rest.startsWith(name));
    if (expressionFunction) {
      tokens.push({ text: expressionFunction, type: 'function' });
      index += expressionFunction.length;
      continue;
    }
    const metricId = metricIds.find((id) => rest.startsWith(id));
    if (metricId) {
      tokens.push({
        label: metricById.get(metricId)?.name || metricId,
        text: metricId,
        type: 'metric',
      });
      index += metricId.length;
      continue;
    }
    if (OPERATOR_PATTERN.test(rest[0])) {
      tokens.push({ text: rest[0], type: 'operator' });
      index += 1;
      continue;
    }
    if (rest[0] === ',') {
      tokens.push({ text: ',', type: 'separator' });
      index += 1;
      continue;
    }
    const unknownMatch = rest.match(/^[^(),+*/\s-]+/);
    tokens.push({ text: unknownMatch[0], type: 'unknown' });
    index += unknownMatch[0].length;
  }
  return tokens;
};

export const extractMetricIdsFromExpression = (expression, metrics = []) => [
  ...new Set(
    tokenizeMetricExpression(expression, metrics)
      .filter((token) => token.type === 'metric')
      .map((token) => token.text),
  ),
];

export const getExpressionMode = (expression) => {
  const source = String(expression || '');
  if (/^\s*BubbleAvailable\s*\(/.test(source)) {
    return 'bubble_available';
  }
  return /^\s*FirstAvailable\s*\(/.test(source) ? 'first_available' : 'arithmetic';
};

export const buildBubbleAvailableExpression = (metricId) =>
  `${BUBBLE_AVAILABLE_FUNCTION}(${metricId || ''})`;

export const buildFirstAvailableExpression = (candidateIds = []) =>
  `${FIRST_AVAILABLE_FUNCTION}(${candidateIds.filter(Boolean).join(', ')})`;

const parseFirstAvailableCandidateIds = (expression, metrics) => {
  const tokens = tokenizeMetricExpression(expression, metrics);
  if (!tokens.some((token) => token.type === 'function')) {
    return null;
  }
  if (
    tokens[0]?.text !== FIRST_AVAILABLE_FUNCTION ||
    tokens[1]?.text !== '(' ||
    tokens.at(-1)?.text !== ')'
  ) {
    throw new Error('FirstAvailable 必须占据整个计算表达式');
  }
  const argumentTokens = tokens.slice(2, -1);
  if (argumentTokens.at(-1)?.type === 'separator') {
    throw new Error('FirstAvailable 参数格式不正确');
  }
  const candidateIds = [];
  for (let index = 0; index < argumentTokens.length; index += 2) {
    const candidate = argumentTokens[index];
    const separator = argumentTokens[index + 1];
    if (
      candidate?.type !== 'metric' ||
      (separator !== undefined && separator?.type !== 'separator')
    ) {
      throw new Error('FirstAvailable 参数格式不正确');
    }
    candidateIds.push(candidate.text);
  }
  if (candidateIds.length < 2) {
    throw new Error('FirstAvailable 至少需要两个候选指标');
  }
  if (new Set(candidateIds).size !== candidateIds.length) {
    throw new Error('FirstAvailable 候选指标不能重复');
  }
  return candidateIds;
};

export const getFirstAvailableCandidateIds = (expression, metrics = []) =>
  parseFirstAvailableCandidateIds(expression, metrics) || [];

export const getFirstAvailableMetrics = (metrics = [], currentMetric = {}) => {
  const currentMetricId = currentMetric.metricId || currentMetric.id;
  return metrics.filter(
    (metric) => metric.id !== currentMetricId && metric.valueType === currentMetric.valueType,
  );
};

const parseBubbleAvailableMetricId = (expression, metrics) => {
  const tokens = tokenizeMetricExpression(expression, metrics);
  if (!tokens.some((token) => token.text === BUBBLE_AVAILABLE_FUNCTION)) {
    return null;
  }
  if (
    tokens[0]?.text !== BUBBLE_AVAILABLE_FUNCTION ||
    tokens[1]?.text !== '(' ||
    tokens.at(-1)?.text !== ')'
  ) {
    throw new Error('BubbleAvailable 必须占据整个计算表达式');
  }
  const argumentTokens = tokens.slice(2, -1);
  if (argumentTokens.length !== 1 || argumentTokens[0]?.type !== 'metric') {
    throw new Error('BubbleAvailable 必须且仅接受一个原子比率指标');
  }
  return argumentTokens[0].text;
};

export const getBubbleAvailableMetricId = (expression, metrics = []) => {
  try {
    return parseBubbleAvailableMetricId(expression, metrics);
  } catch {
    return null;
  }
};

export const getBubbleAvailableMetrics = (metrics = [], currentMetric = {}) => {
  const currentMetricId = currentMetric.metricId || currentMetric.id;
  return metrics.filter(
    (metric) =>
      metric.id !== currentMetricId &&
      metric.enabled !== false &&
      metric.metricType === 'atomic' &&
      metric.valueType === 'ratio',
  );
};

export const appendExpressionToken = (expression, token) =>
  [String(expression || '').trim(), token].filter(Boolean).join(' ');

export const removeExpressionTokenAt = (expression, tokenIndex, metrics = []) =>
  tokenizeMetricExpression(expression, metrics)
    .filter((_, index) => index !== tokenIndex)
    .map((token) => token.text)
    .join(' ');

const inferExpressionValueType = (tokens, metricById) => {
  let index = 0;
  const peek = () => tokens[index]?.text;
  const consume = () => tokens[index++];

  const parseFactor = () => {
    const token = consume();
    if (token?.text === '(') {
      const valueType = parseAdditive();
      if (consume()?.text !== ')') {
        throw new Error('计算表达式括号不匹配');
      }
      return valueType;
    }
    const metric = token?.type === 'metric' ? metricById.get(token.text) : null;
    if (!metric) {
      throw new Error('计算表达式缺少指标');
    }
    return metric.valueType;
  };

  const parseMultiplicative = () => {
    let valueType = parseFactor();
    while (peek() === '*' || peek() === '/') {
      const operator = consume().text;
      const rightValueType = parseFactor();
      if (operator === '*') {
        if (valueType !== 'ratio' && rightValueType !== 'ratio') {
          throw new Error('乘法至少一侧必须是比率');
        }
        valueType = valueType === 'ratio' ? rightValueType : valueType;
        continue;
      }
      if (valueType === rightValueType) {
        valueType = 'ratio';
      } else if (rightValueType !== 'ratio' || !['amount', 'quantity'].includes(valueType)) {
        throw new Error('除法仅支持同类型相除，或金额、数量除以比率');
      }
    }
    return valueType;
  };

  const parseAdditive = () => {
    const valueType = parseMultiplicative();
    while (peek() === '+' || peek() === '-') {
      consume();
      const rightValueType = parseMultiplicative();
      if (valueType !== rightValueType) {
        throw new Error('加减运算两侧必须使用同一种值类型');
      }
    }
    return valueType;
  };

  const valueType = parseAdditive();
  if (index !== tokens.length) {
    throw new Error('计算表达式仅支持加、减、乘、除法和括号');
  }
  return valueType;
};

export const getAvailableExpressionMetrics = (metrics = [], currentMetric = {}) =>
  metrics.filter((metric) => metric.id !== currentMetric.metricId);

export const validateMetricExpression = (expression, metrics = [], currentMetric = {}) => {
  const tokens = tokenizeMetricExpression(expression, metrics);
  if (tokens.length === 0) {
    return '请输入计算表达式';
  }
  if (tokens.some((token) => token.type === 'function')) {
    if (tokens[0]?.text === BUBBLE_AVAILABLE_FUNCTION) {
      let metricId;
      try {
        metricId = parseBubbleAvailableMetricId(expression, metrics);
      } catch (error) {
        return error.message;
      }
      const currentMetricId = currentMetric.metricId || currentMetric.id;
      if (currentMetricId && metricId === currentMetricId) {
        return '计算表达式不能引用当前指标';
      }
      const metric = metrics.find((candidate) => candidate.id === metricId);
      if (
        metric?.enabled === false ||
        metric?.metricType !== 'atomic' ||
        metric?.valueType !== 'ratio'
      ) {
        return 'BubbleAvailable 参数必须是已启用的原子比率指标';
      }
      return currentMetric.valueType && currentMetric.valueType !== 'ratio'
        ? 'BubbleAvailable 结果指标必须是比率'
        : null;
    }
    let candidateIds;
    try {
      candidateIds = parseFirstAvailableCandidateIds(expression, metrics);
    } catch (error) {
      return error.message;
    }
    const currentMetricId = currentMetric.metricId || currentMetric.id;
    if (currentMetricId && candidateIds.includes(currentMetricId)) {
      return '计算表达式不能引用当前指标';
    }
    const metricById = new Map(metrics.map((metric) => [metric.id, metric]));
    const candidates = candidateIds.map((metricId) => metricById.get(metricId));
    if (candidates.some((metric) => metric.valueType !== (currentMetric.valueType || 'amount'))) {
      return 'FirstAvailable 候选指标值类型必须与目标指标一致';
    }
    return null;
  }
  if (tokens.some((token) => token.type === 'unknown')) {
    return '计算表达式中包含未知指标或符号';
  }
  if (
    currentMetric.metricId &&
    tokens.some((token) => token.type === 'metric' && token.text === currentMetric.metricId)
  ) {
    return '计算表达式不能引用当前指标';
  }
  const metricById = new Map(metrics.map((metric) => [metric.id, metric]));
  try {
    if (inferExpressionValueType(tokens, metricById) !== (currentMetric.valueType || 'amount')) {
      throw new Error('计算表达式结果类型必须与目标指标一致');
    }
  } catch (error) {
    return error.message;
  }
  return null;
};
